import './styles/ImageCropper.scss';
import { AppContext } from '../Context';
import { useContext, useState, useRef, useEffect } from 'react';

//muis
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import OpenInNewOfIcon from '@mui/icons-material/OpenInNewOff';

//classes
class Position {
    x: number = 0;
    y: number = 0;
}

const nonstate = {
    frameMinSize: 25, //200px
    sensitivity: .05 //  0.N - 1, smaller number less sensitive 
}

interface Props {
    src: string, SetSrc: any, CropAction: any
    positionMode?: string //'start', 'center', 'end'
}

const ImageCropper: React.FC<Props> = ({ src, SetSrc, CropAction, positionMode }) => {
    //contexts
    const appContext = useContext(AppContext);
    //states
    const [subjectWidth, SetSubjectWidth] = useState(0);
    const [subjectHeight, SetSubjectHeight] = useState(0);
    const [subjectOrientation, SetSubjectOrientation] = useState('');  //portrait, landscape
    const [frameTop, SetFrameTop] = useState(0);
    const [frameLeft, SetFrameLeft] = useState(0);
    const [frameWidth, SetFrameWidth] = useState(0);
    const [frameHeight, SetFrameHeight] = useState(0);
    const [frameSrc, SetFrameSrc] = useState('');
    //refs - objs
    const hiddenSubjectImgRef = useRef<any>(null);
    const subjectImgRef = useRef<any>(null);
    const frameRef = useRef<any>(null);
    const frameImgRef = useRef<any>(null);
    const canvasRef = useRef<any>(null);
    //refs - vars(frame)
    const frameTopRef = useRef<number>(frameTop);
    const frameLeftRef = useRef<number>(frameLeft);
    const frameWidthRef = useRef<number>(frameWidth);
    const frameHeightRef = useRef<number>(frameHeight);
    //refs - vars
    const subjectLoadedRef = useRef<boolean>(false);
    const initialTouchPosRef = useRef<Position>({ x: 0, y: 0 } as Position);
    const currentTouchedPointerRef = useRef<number>(-1);
    const frameInitializedRef = useRef<boolean>(false);

    //watchers
    //test
    // useEffect(() => {
    //     console.log('ImageCropperTest: ' + frameHeight);
    // }, [frameHeight]);

    //watch for initialize, turn navbar on and off
    useEffect(() => {
        appContext.SetNavBarShouldHide(true);
        appContext.SetHeaderShouldHide(true);

        return(() => {
            appContext.SetNavBarShouldHide(false);
            appContext.SetHeaderShouldHide(false);
            frameInitializedRef.current = false;
        });
    }, []);

    //watch for subject orientation update, update framPosition based on positionMode
    useEffect(() => {
        if(
            subjectOrientation != ''
            && frameWidth != 0 && frameHeight != 0
            && subjectWidth != 0 && subjectHeight != 0
        ) {
            if(positionMode == undefined || positionMode == 'center') {
                if(subjectOrientation == 'portrait') {
                    frameTopRef.current = subjectHeight/2 - frameHeight/2;
                } else if(subjectOrientation == 'landscape') {
                    frameLeftRef.current = subjectWidth/2 - frameWidth/2;
                }
            } else if(positionMode == 'start') {
                if(subjectOrientation == 'portrait') {
                    frameTopRef.current = 0;
                } else if(subjectOrientation == 'landscape') {
                    frameLeftRef.current = 0;
                }            
            } else if(positionMode == 'end') {
                if(subjectOrientation == 'portrait') {
                    frameTopRef.current = subjectHeight - frameHeight;
                } else if(subjectOrientation == 'landscape') {
                    frameLeftRef.current = subjectWidth - frameWidth;
                }
            }
            SetFrameTop(frameTopRef.current);
            SetFrameLeft(frameLeftRef.current);
            frameInitializedRef.current = true;
        }
    }, [subjectOrientation, frameWidth, frameHeight, subjectWidth, subjectHeight]);

    //watch for frame change redrawFrame
    useEffect(() => {
        if (subjectLoadedRef.current) {
            //recapture - redraw frame image
            if (canvasRef.current == null)
                canvasRef.current = document.createElement('canvas');

            canvasRef.current.width = frameWidth;
            canvasRef.current.height = frameHeight;
            const ctx = canvasRef.current.getContext('2d');
            const x_scale = subjectImgRef.current.naturalWidth / subjectImgRef.current.width;
            const y_scale= subjectImgRef.current.naturalHeight / subjectImgRef.current.height;

            ctx.clearRect(0, 0, frameWidth, frameHeight);
            // drawImage measure clipping image based on image natural width and height instead of the img tag width and height
            ctx.drawImage(
                subjectImgRef.current,
                Math.floor(frameLeft * x_scale), Math.floor(frameTop * y_scale),
                Math.floor(frameWidth * x_scale) , Math.floor(frameHeight * y_scale),
                0, 0,
                frameWidth, frameHeight);
            SetFrameSrc(canvasRef.current.toDataURL());
        }
    }, [frameTop, frameLeft, frameWidth, frameHeight]);

    //ui functions
    const OnSubjectLoaded = () => {
        subjectLoadedRef.current = true;
        const w_h_ratio = hiddenSubjectImgRef.current.naturalWidth / hiddenSubjectImgRef.current.naturalHeight;

        if (hiddenSubjectImgRef.current.naturalWidth > hiddenSubjectImgRef.current.naturalHeight) {
            //isLandscape
            SetSubjectOrientation('landscape');
            // SetFrameWidth(window.innerWidth);
            // SetFrameHeight(Math.floor(window.innerWidth / w_h_ratio));
            frameWidthRef.current = window.innerWidth;
            frameHeightRef.current = Math.floor(window.innerWidth / w_h_ratio);
            SetSubjectWidth(window.innerWidth);
            SetSubjectHeight(Math.floor(window.innerWidth / w_h_ratio));
        } else {
            //isPortrait
            SetSubjectOrientation('portrait');

            let adjustedHeight = window.innerHeight * .75;
            if(Math.floor(window.innerWidth / w_h_ratio) < window.innerHeight * .75)
                adjustedHeight =  Math.floor(window.innerWidth / w_h_ratio);
            let adjustedWidth = adjustedHeight * w_h_ratio;

            // SetFrameHeight(adjustedWidth);
            // SetFrameWidth(adjustedWidth);
            frameWidthRef.current = adjustedWidth;
            frameHeightRef.current = adjustedWidth;
            SetSubjectHeight(adjustedHeight);
            SetSubjectWidth(adjustedWidth);
        }
        SetFrameWidth(frameWidthRef.current);
        SetFrameHeight(frameHeightRef.current);
    }
    const OnPointer0TouchMove = (event: any) => {
        const movePosition = { x: event.touches[0].clientX, y: event.touches[0].clientY } as Position;
        const moveX = (movePosition.x - initialTouchPosRef.current.x) * nonstate.sensitivity;
        const moveY = (movePosition.y - initialTouchPosRef.current.y) * nonstate.sensitivity;

        //x check
        if (
            frameLeftRef.current + moveX > 0
            && (frameLeftRef.current + moveX) < (subjectImgRef.current.width - frameWidthRef.current)
        ) {
            frameLeftRef.current += moveX;
        } else {
            //hit left and right boundary
            if (frameLeftRef.current + moveX <= 0)
                frameLeftRef.current = 0;
            else
                frameLeftRef.current = subjectImgRef.current.width - frameWidthRef.current;
        }
        SetFrameLeft(frameLeftRef.current);

        //y check
        if (
            frameTopRef.current + moveY > 0
            && (frameTopRef.current + moveY) < (subjectImgRef.current.height - frameHeightRef.current)
        ) {
            frameTopRef.current += moveY;
        } else {
            //hit top and bottom boundary
            if (frameTopRef.current + moveY <= 0)
                frameTopRef.current = 0;
            else
                frameTopRef.current = subjectImgRef.current.height - frameHeightRef.current;
        }
        SetFrameTop(frameTopRef.current);
    }
    const OnPointerTouchStart = (event: any, pointer: number) => {
        initialTouchPosRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY } as Position;
        currentTouchedPointerRef.current = pointer;
    }
    const OnPointerTouchMove = (event: any, pointer: number) => {
        if (pointer == currentTouchedPointerRef.current) {
            const movePos = { x: event.touches[0].clientX, y: event.touches[0].clientY } as Position;
            const moveX = movePos.x - initialTouchPosRef.current.x;
            const moveY = movePos.y - initialTouchPosRef.current.y;
            let dynamic = 0;
            if (moveX > moveY)
                dynamic = moveX * nonstate.sensitivity;
            else
                dynamic = moveY * nonstate.sensitivity;

            switch (pointer) {
                case 1: //bottom right
                    if (
                        frameWidthRef.current + dynamic >= nonstate.frameMinSize
                        && frameWidthRef.current + dynamic <= subjectImgRef.current.width - frameLeftRef.current
                        && frameHeightRef.current + dynamic <= subjectImgRef.current.height - frameTopRef.current
                    ) {
                        frameWidthRef.current += dynamic;
                        frameHeightRef.current += dynamic;
                    } else if(frameWidthRef.current + dynamic < nonstate.frameMinSize) {
                        frameWidthRef.current = nonstate.frameMinSize;
                        frameHeightRef.current = nonstate.frameMinSize;
                    } else {
                        let diffX = dynamic; let diffY = dynamic;
                        if(frameWidthRef.current + dynamic > subjectImgRef.current.width - frameLeftRef.current)
                            diffX = subjectImgRef.current.width - frameLeftRef.current - frameWidthRef.current;
                        if(frameHeightRef.current + dynamic > subjectImgRef.current.height - frameTopRef.current)
                            diffY = subjectImgRef.current.height - frameTopRef.current - frameHeightRef.current;

                        if(diffX < diffY) {
                            frameWidthRef.current += diffX;
                            frameHeightRef.current += diffX;
                        } else {
                            frameWidthRef.current += diffY;
                            frameHeightRef.current += diffY;
                        }
                    }
                    break;
            }
            SetFrameWidth(frameWidthRef.current);
            SetFrameHeight(frameHeightRef.current);
        }
    }
    const OnCropBtnClicked = () => {
        CropAction(canvasRef.current.toDataURL());
        SetSrc('');
    }

    return (
        <div className='ImageCropper'>
            <img className='hidden-subject-img' ref={hiddenSubjectImgRef} src={src} onLoad={() => OnSubjectLoaded()} />
            <div className='container' style={{ width: subjectWidth, height: subjectHeight }}>
                <img className='subject-img' ref={subjectImgRef} src={src} style={{ width: subjectWidth, height: subjectHeight }}/>
                <div
                    className='frame'
                    ref={frameRef}
                    style={{ top: frameTop, left: frameLeft, width: frameWidth, height: frameHeight }}
                >
                    <img className='frame-img' ref={frameImgRef} src={frameSrc}/>
                    <div
                        className='pointer-0'
                        onTouchStart={(event: any) => { initialTouchPosRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY } as Position }}
                        onTouchMove={(event: any) => OnPointer0TouchMove(event)}
                    />
                    <div className='pointer-1' onTouchStart={(event: any) => OnPointerTouchStart(event, 1)} onTouchMove={(event: any) => OnPointerTouchMove(event, 1)}>
                        <OpenInNewOfIcon />
                    </div>
                    {/* <button className='pointer-2' onTouchStart={(event: any) => OnPointerTouchStart(event, 2)} onTouchMove={(event: any) => OnPointerTouchMove(event, 2)}>
                    </button>
                    <button className='pointer-3' onTouchStart={(event: any) => OnPointerTouchStart(event, 3)} onTouchMove={(event: any) => OnPointerTouchMove(event, 3)}>
                    </button>
                    <button className='pointer-4' onTouchStart={(event: any) => OnPointerTouchStart(event, 4)} onTouchMove={(event: any) => OnPointerTouchMove(event, 4)}>
                    </button> --currently only allow bottom right corner, other corners implement in future */}
                </div>
            </div>
            <button className='close-btn' onClick={() => SetSrc('')}><CloseIcon /></button>
            <button className='crop-btn' onClick={() => OnCropBtnClicked()}>Ok <CropIcon /></button>
        </div>
    )
}
export default ImageCropper;